import {useCallback, useMemo} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {optimisticallyUpdate} from 'lib/OptimisticallyUpdate';
import {
  AGENT_SKILLS_QUERY,
  AgentSkillsQueryData,
  archiveSkill as archiveSkillApi,
  bulkCreateSkills,
  disableSkill as disableSkillApi,
  enableSkill as enableSkillApi,
  SkillSummary,
  writeSkillIsEnabledToCache,
} from 'llm/chat-library/skills/SkillsDAO';
import * as ImportUtil from 'util/ImportUtil';

interface UseSkillsResult {
  skills: Array<SkillSummary>;
  isLoading: boolean;
  archiveSkill: (skillId: string, reason: string) => Promise<void>;
  createNewSkills: (files: Array<File>) => Promise<void>;
  refresh: () => Promise<void>;
  enableSkill: (skillId: string) => Promise<void>;
  disableSkill: (skillId: string) => Promise<void>;
}

export default function useSkills({enabled = true}: {enabled?: boolean} = {}): UseSkillsResult {
  const queryResult = useSkippableBenchlingQuery<AgentSkillsQueryData>(AGENT_SKILLS_QUERY, {
    skip: !enabled,
    fetchPolicy: 'network-only',
  });

  const skills: Array<SkillSummary> = useMemo(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return [];
    }
    return queryResult.data.agentSkills.map((skill) => ({
      id: skill.id,
      name: skill.name,
      description: skill.description,
      createdAt: skill.createdAt,
      isOwnedByCurrentUser: skill.permissions.owner,
      canManageAccess: skill.permissions.admin,
      // TODO: Update this when we support community-authored skills. Currently all
      // extension-installed skills are Benchling-authored, so we hardcode "Benchling"
      // to avoid showing "Benchling Service Maintenance" (the service account name).
      creatorName: skill.extensionDefinitionId != null ? 'Benchling' : (skill.creatorName ?? undefined),
      extensionDefinitionId: skill.extensionDefinitionId,
      isEnabled: skill.isEnabled,
      skillFiles: skill.skillFiles,
      owner: skill.ownerJson,
    }));
  }, [queryResult]);

  const isLoading = !enabled ? false : queryResult.type === QueryResultType.PENDING;

  const archiveSkillHandler = useCallback(
    async (skillId: string, reason: string) => {
      await archiveSkillApi(skillId, reason);
      if (queryResult.type === QueryResultType.SUCCESS) {
        await queryResult.refetch();
      }
    },
    [queryResult]
  );

  const createNewSkills = useCallback(
    async (files: Array<File>) => {
      const s3Locations = await Promise.all(
        files.map(async (file) => {
          const location = await ImportUtil.upload(file);
          if (location == null) {
            throw new Error(`Failed to upload file: ${file.name}`);
          }
          return location;
        })
      );
      await bulkCreateSkills(s3Locations);
      if (queryResult.type === QueryResultType.SUCCESS) {
        await queryResult.refetch();
      }
    },
    [queryResult]
  );

  const refresh = useCallback(async () => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      await queryResult.refetch();
    }
  }, [queryResult]);

  const enableSkillHandler = useCallback(async (skillId: string) => {
    await optimisticallyUpdate({
      persistData: () => enableSkillApi(skillId),
      writeDataToCache: (isEnabled: boolean) => {
        writeSkillIsEnabledToCache(skillId, isEnabled);
      },
      optimisticData: true,
      revertData: false,
    });
  }, []);

  const disableSkillHandler = useCallback(async (skillId: string) => {
    await optimisticallyUpdate({
      persistData: () => disableSkillApi(skillId),
      writeDataToCache: (isEnabled: boolean) => {
        writeSkillIsEnabledToCache(skillId, isEnabled);
      },
      optimisticData: false,
      revertData: true,
    });
  }, []);

  return {
    skills,
    isLoading,
    archiveSkill: archiveSkillHandler,
    createNewSkills,
    refresh,
    enableSkill: enableSkillHandler,
    disableSkill: disableSkillHandler,
  };
}
